import { useState, useCallback, useRef } from 'react';
import salesService from '../services/salesService';
import ordersService from '../services/ordersService';
import i18n from '../i18n';

function getDefaultDateRange() {
  const today = new Date().toISOString().split('T')[0];
  return {
    from: today,
    to: today,
  };
}

export default function useSales() {
  const [sales, setSales] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState(getDefaultDateRange());
  const activeRequestRef = useRef(null);

  const fetchSales = useCallback(async (fromDate, toDate, params = {}) => {
    if (!fromDate || !toDate) {
      setError(i18n.t('hookErrors.common.dateRangeRequired'));
      return;
    }

    const requestId = Date.now();
    activeRequestRef.current = requestId;

    setLoading(true);
    setError(null);

    try {
      const res = await salesService.getSalesDaybook(fromDate, toDate, params);
      if (activeRequestRef.current !== requestId) return;

      if (res?.success) {
        const raw = res.data;
        if (Array.isArray(raw)) {
          setSales(raw);
          setTotal(raw.length);
        } else {
          setSales(raw?.records || raw?.items || []);
          setTotal(raw?.total ?? raw?.count ?? 0);
        }
      } else {
        setError(res?.error || i18n.t('hookErrors.sales.fetchData'));
        setSales([]);
      }
    } catch (err) {
      if (activeRequestRef.current !== requestId) return;
      setError(err?.message || i18n.t('hookErrors.sales.fetchData'));
      setSales([]);
    } finally {
      if (activeRequestRef.current === requestId) {
        setLoading(false);
        activeRequestRef.current = null;
      }
    }
  }, []);

  const updateDateRange = useCallback((from, to) => {
    if (!from || !to) return;
    setDateRange({ from, to });
  }, []);

  const exportSalesDaybook = useCallback(async (fromDate, toDate, params = {}) => {
    try {
      const res = await salesService.downloadSalesDaybook(fromDate, toDate, params);
      if (res.success) {
        // Create a blob URL and trigger download
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = url;
        
        // Try to get filename from content-disposition header
        let filename = `sales_daybook_${fromDate}_to_${toDate}.csv`;
        const contentDisposition = res.headers?.['content-disposition'];
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1];
          }
        }
        
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        return { success: true };
      }
      return { success: false, error: res.error };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  const bulkDeleteSales = useCallback(async (orderIds, password) => {
    if (!Array.isArray(orderIds) || orderIds.length === 0) return { success: false, error: i18n.t('hookErrors.noSelection.sales') };
    setLoading(true);
    setError(null);
    try {
      // Using ordersService.bulkDeleteSelectedOrders as sales are essentially invoiced orders
      const res = await ordersService.bulkDeleteSelectedOrders({ order_ids: orderIds, password });
      if (res.success) {
        setSales(prev => prev.filter(s => !orderIds.includes(s.order_id || s.id)));
        setTotal(prev => Math.max(0, prev - orderIds.length));
      } else {
        setError(res?.error || i18n.t('hookErrors.sales.bulkDelete'));
      }
      return res;
    } catch (err) {
      setError(err?.message || i18n.t('hookErrors.sales.bulkDelete'));
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    sales,
    total,
    loading,
    error,
    dateRange,
    fetchSales,
    updateDateRange,
    exportSalesDaybook,
    bulkDeleteSales,
  };
}
